import { itemPricingFields, lockedItemFieldsFor, PurchaseOrderStatus, StageItemDetail, Task, TaskStatus, StatusPickerOption, Party, findPendingLineTaskRefusal, isTaskLockedForParty } from '@jms/model';
import type { ItemEditorField, ItemFieldRuleFacts } from './job.item.model';
import { financeLockHint, invoicingForcesZeroSell, payableForcesZeroBuy } from '@workbuddy/surfaces/embedded-item';

// Item field readonly policy. Shared by the board editors and the Detail surface.

/**
 * Which of the editor's fields are readonly.
 *
 * `readonly()` mirrors the update command's own `assertCanPatchItem` through the same shared field
 * lists, so the editor can never offer a field the write refuses. The extras below it are legacy's,
 * which are stricter — a control disabled where the API would accept it is the safe direction, and it
 * is the behaviour users are moving off the legacy tab with.
 *
 * Missing capabilities read as readonly, which is what an unloaded item should be.
 */
export function resolveItemFieldReadonly(facts: ItemFieldRuleFacts): Record<ItemEditorField, boolean> {

  const { capabilities, lockedFields, approvedPurchaseOrder, hasPurchaseOrder, canOverrideDesc, sellPriceForced, buyPriceForced, billableLockedByParent, payableLockedByParent } = facts;

  const locked = lockedFields.length > 0 && !capabilities?.canModifyLockedItems;

  // Not `string`: every caller below passes a real editor field, so a typo should fail to compile.
  const readonly = (field: ItemEditorField): boolean => {

    // Without the section's Edit right every field locks, status included (WBS-12074).
    if (capabilities?.canEditSection === false) return true;
    if (field === 'note' && capabilities?.canUpdateNote) return false;
    if (!capabilities?.canUpdate) return true;
    if (itemPricingFields.includes(field) && !capabilities.canModifyPricing) return true;

    // Description and long description are legacy's addition to the server's locked list.
    return locked && (lockedFields.includes(field) || field === 'description' || field === 'longDescription');
  };

  return {
    location: readonly('location'),
    note: readonly('note'),
    description: readonly('description') || !canOverrideDesc,
    longDescription: readonly('longDescription') || !canOverrideDesc,
    quantity: readonly('quantity') || approvedPurchaseOrder,
    codeUoM: readonly('codeUoM') || approvedPurchaseOrder,
    // A parent mark locks the answer AND the price on its own side: the mark forces both, and the
    // write refuses either. `sellPriceForced` only follows the line's own answer, which stays editable.
    buyPrice: readonly('buyPrice') || approvedPurchaseOrder || buyPriceForced || payableLockedByParent,
    // Legacy never locked the sell side on an approved order: the buyer's price is fixed, the client's is not.
    sellPrice: readonly('sellPrice') || sellPriceForced || billableLockedByParent,
    // Status never moves from an editor, whatever the rights (WBS-12286): the row menu's Cancel / Change Status own it.
    status: true,
    codeInvoicingStatus: readonly('codeInvoicingStatus') || billableLockedByParent,
    suppliedBySubcontractor: readonly('suppliedBySubcontractor') || hasPurchaseOrder,
    // No extra clause: the buy-side lock list already covers the finance-linked case, and staying out of
    // itemPricingFields is deliberate — the same split codeInvoicingStatus has.
    payable: readonly('payable') || payableLockedByParent,
    // The line's own custom fields follow the line: whoever may edit the item may answer them.
    lineCustomFields: readonly('lineCustomFields'),
  };
}

/**
 * Options for {@link jobItemReadonly}.
 *
 * `ignoreOwnPriceForcing` drops the line's OWN Billable / Payable answer from the two price gates and
 * keeps every static lock. The row editor needs it: its gates are read once at mount, so an answer
 * baked in there would still be the loaded one after the user changed it, and the price box could
 * never unlock. That editor re-states the forcing as a live model rule instead. Surfaces that recompute
 * their gates from a signal on every change leave it off.
 */
export interface JobItemReadonlyOptions {
  readonly canOverrideDesc?: boolean;
  readonly ignoreOwnPriceForcing?: boolean;
  /** Contractor portal host: see {@link contractorItemReadonly}. */
  readonly contractor?: boolean;
}

/** Facts for {@link resolveItemFieldReadonly} from a live item. Missing `canOverrideDesc` fails open unless `extras` overrides. */
function jobItemFieldFacts(item: StageItemDetail | null | undefined, extras?: JobItemReadonlyOptions): ItemFieldRuleFacts {

  if (!item) {
    return {
      lockedFields: [],
      approvedPurchaseOrder: false,
      hasPurchaseOrder: false,
      canOverrideDesc: extras?.canOverrideDesc === true,
      sellPriceForced: false,
      buyPriceForced: false,
      billableLockedByParent: false,
      payableLockedByParent: false,
    };
  }

  const purchaseOrderStatus = (item.purchaseOrder as { status?: PurchaseOrderStatus } | undefined)?.status;
  return {
    capabilities: item.capabilities,
    lockedFields: lockedItemFieldsFor(item),
    approvedPurchaseOrder: purchaseOrderStatus === PurchaseOrderStatus.Approved,
    hasPurchaseOrder: !!item.purchaseOrder?._id,
    canOverrideDesc: extras?.canOverrideDesc ?? item.canOverrideDesc !== false,
    sellPriceForced: extras?.ignoreOwnPriceForcing === true ? false : invoicingForcesZeroSell(item.codeInvoicingStatus),
    buyPriceForced: extras?.ignoreOwnPriceForcing === true ? false : payableForcesZeroBuy(item.payable),
    // Resolved server-side against this item's job and stage: a board row carries no job, so the
    // editor cannot work out on its own why a side is forced.
    billableLockedByParent: financeLockHint(item.financeLock, 'billable') !== null,
    payableLockedByParent: financeLockHint(item.financeLock, 'payable') !== null,
  };
}

export function jobItemReadonly(item: StageItemDetail | null | undefined, extras?: JobItemReadonlyOptions): Record<ItemEditorField, boolean> {

  if (extras?.contractor) return contractorItemReadonly(item);

  return resolveItemFieldReadonly(jobItemFieldFacts(item, extras));
}

/**
 * A subcontractor changes quantity, location and instruction on their own Draft, as the legacy row
 * did; every other field is the office's, and the server refuses a change to any of them. The server's
 * `canUpdate` already holds the Draft author window.
 */
function contractorItemReadonly(line: StageItemDetail | null | undefined): Record<ItemEditorField, boolean> {

  const locked = contractorItemLocked(line);

  return {
    location: locked,
    note: locked,
    quantity: locked,
    description: true,
    longDescription: true,
    codeUoM: true,
    buyPrice: true,
    sellPrice: true,
    status: true,
    codeInvoicingStatus: true,
    suppliedBySubcontractor: true,
    payable: true,
    lineCustomFields: true,
  };
}

/** The server's `canUpdate` holds the Draft author window; everything a contractor may change on a line closes with it. */
function contractorItemLocked(line: StageItemDetail | null | undefined): boolean {

  return line?.capabilities?.canUpdate !== true;
}

export interface JobItemEvidenceFacts {
  readonly line: Pick<StageItemDetail, 'isClaimCode' | 'resolvedStatus'> | null | undefined;
  readonly task: Pick<Task, '_id' | 'status' | 'isReadonly'> | null | undefined;
  /** The live Tasks status the task's stamp names; its `editableBy` is what the stamp does not carry. */
  readonly taskStatus: Pick<StatusPickerOption, 'editableBy' | 'statusType'> | null | undefined;
  /** Portal only: a Tasks set read succeeded; without it a missing `taskStatus` would pass as a legacy task and open the upload. */
  readonly taskStatusesRead: boolean;
  readonly contractor: boolean;
  /** Portal only: `stageIsOpenForContractor`, the stage half of the server's evidence gate (`resolveContractorTaskEvidenceRefusal`). */
  readonly stageOpenForContractor: boolean;
  readonly canManageManualTasks: boolean;
  readonly surfaceReadonly: boolean;
}

/** Evidence follows the item task, not the line lock: an authorised SOR is locked for editing but its open task still needs After photos (WBS-12033). */
export function jobItemEvidenceReadonly(facts: JobItemEvidenceFacts): boolean {

  if (facts.surfaceReadonly || !facts.task?._id || facts.task.isReadonly === true) return true;

  // The portal asks what `ContractorTasksLogic.addAttachments` asks, through the same model rules, so the drop-zone shows only where the server will store the photo.
  if (facts.contractor) {
    return !facts.taskStatusesRead
      || !facts.stageOpenForContractor
      || !facts.canManageManualTasks
      || isTaskLockedForParty(facts.taskStatus, facts.task, Party.Contractor)
      || (facts.line?.isClaimCode === true && findPendingLineTaskRefusal(facts.line.resolvedStatus, Party.Contractor) !== undefined);
  }

  return facts.task.status === TaskStatus.Completed || !facts.canManageManualTasks;
}
